import React from 'react';

import {cssClasses, identity, pretty} from "../../shared/utils";
import kindof from "kind-of";
import {kebabCase} from "lodash";
import {Toggles} from "../../shared/toggles";
import initForm from "../../react-components/forms";

const VALIDATE_ID_PATTERN = /^[\w\d-]{0,64}$/;

function validateRuleId(id) {
	id = String(id);
	if (!VALIDATE_ID_PATTERN.test(id)) {
		throw new Error(`Invalid ID`);
	}
	return id;
}

function validate3DRate(rate) {
	rate = Number(rate);
	if (isNaN(rate) || rate < 0 || rate > 100) {
		throw new Error(`Invalid 3D rate value`);
	}
	return rate;
}

function makeLabel(id, formatter = identity) {
	let t = kindof(id);
	if (t === 'array') {
		return Array.from(id).slice(0, 2);
	}
	if (t === 'object') {
		let k = id.key;
		if (k) {
			// guess it's a toggle...
			let v = id.value || k;
			return [k, v];
		}
	}
	return [id, formatter(id)];
}

function parseConfig(cfg) {
	let {filters = [], rates = []} = cfg;
	let filterSelectorsProps = [];
	for (let [key, options] of filters) {
		let [path, label] = makeLabel(key);
		let reactKey = kebabCase(`filter-${key}`);
		let toggles = new Toggles(options, {notEmpty: true});
		filterSelectorsProps.push({key: reactKey, path, label, options: toggles});
	}
	let rateControlsProps = [];
	for (let [key, options] of rates) {
		let [path, label] = makeLabel(key);
		let reactKey = kebabCase(`rates-group-${key}`);
		rateControlsProps.push({key: reactKey, path, label, options: options});
	}
	return {filterSelectorsProps, rateControlsProps};
}

const {Form, FormGroup, Input, TogglesSelect, useFormState} = initForm('central-config');

function FilterSelector(props) {
	let {path, label, options} = props;
	return (
		<div className="form-group">
			<label>{label}</label>
			<TogglesSelect path={path} options={options}/>
		</div>
	);
}

function FilterDisplay(props) {
	let {path, label} = props;
	let [state] = useFormState();
	let value = state.getValue(path);
	if (Array.isArray(value)) {
		value = value.join(', ');
	}
	return <Display label={label}>{value}</Display>
}

function RateInput(props) {
	let {path, label, validate, className} = props;
	return (
		<div className={cssClasses('cm-input', className)}>
			<label>{label}: </label>
			<Input
				type="number" min="0" max="100"
				path={path} placeholder="3D rate"
				validate={validate}
			/>
		</div>
	);
}

function RatesControls(props) {
	let {path: ratePath, label, options} = props;
	return (
		<>
			<h3>{label}</h3>
			<div className="cm-rates-controls">
				{
					options.map(t => {
						let [path, label] = makeLabel(t);
						path = [ratePath, path];
						let reactKey = kebabCase(`rate-option-${ratePath}-${path}`);
						return <RateInput
							key={reactKey} path={path} label={label}
							validate={validate3DRate}
						/>
					})
				}
			</div>
		</>
	);
}

function Alert(props) {
	let {icon, children, context = 'info', className} = props;
	return (
		<div className={cssClasses('alert', `alert-${context}`, className)} role='alert'>
			{icon && (<i className={cssClasses('fa', `fa-${icon}`)}/>)}
			{' '}
			{children}
		</div>
	);
}

function Display(props) {
	let {children, label, className} = props;
	if (!children) {
		return null;
	}
	return (
		<div className={cssClasses('cm-display', className)}>
			{label && <span className="cm-label">{label}:</span>}
			{' '}
			<span className="cm-value">{children}</span>
		</div>
	);
}

export {
	validateRuleId, validate3DRate, makeLabel, parseConfig,
	Form, FormGroup, Input, TogglesSelect,
	FilterSelector, FilterDisplay, RateInput, RatesControls, Alert, Display
}